// src/component/common/FaviconImage.jsx - Fixed version
import React, { useState, useMemo, useCallback } from 'react';
import { Globe } from 'lucide-react';

// faviconUrl is still accepted so existing callers keep working, but it is
// deliberately ignored — see the comment on faviconSrc below.
const FaviconImage = ({ domain, size = 16, className = "" }) => {
  const [faviconError, setFaviconError] = useState(false);

  // Clean domain helper
  const cleanDomainName = useCallback((domainInput) => {
    if (!domainInput || typeof domainInput !== 'string') {
      return null;
    }

    let cleanDomain = domainInput;
    try {
      if (domainInput.includes('://')) {
        cleanDomain = new URL(domainInput).hostname;
      }
      cleanDomain = cleanDomain.replace(/^www\./, '');
    } catch (e) {
      cleanDomain = domainInput.replace(/^www\./, '');
    }

    return cleanDomain;
  }, []);

  // Stored favicon_url values are all unusable: they point either at the retired
  // 64.227.151.19:8012 host (dead, and plain http on an https app), at the
  // question.webp placeholder, or at a relative backend/favicons/ path that no
  // route serves — that last one returns index.html with a 200, so the <img>
  // gets an HTML body and stays broken. Build straight from the domain instead.
  const faviconSrc = useMemo(() => {
    if (faviconError || !domain) return null;
    const cleanDomain = cleanDomainName(domain);
    if (!cleanDomain) return null;
    return `https://www.google.com/s2/favicons?domain=${cleanDomain}&sz=${size}`;
  }, [domain, size, faviconError, cleanDomainName]);

  const handleFaviconError = () => {
    if (!faviconError) setFaviconError(true);
  };

  const handleImageLoad = (e) => {
    // Check if image is too small (likely a placeholder from Google)
    if (e.target.naturalWidth <= 1 || e.target.naturalHeight <= 1) {
      handleFaviconError();
    }
  };

  // Show fallback if no favicon source or no domain
  if (!faviconSrc || !domain) {
    return (
      <div
        className={`bg-gray-200 rounded-sm flex items-center justify-center ${className}`}
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        <Globe className="w-3 h-3 text-gray-400" />
      </div>
    );
  }

  return (
    <img
      src={faviconSrc}
      alt={`${domain} favicon`}
      className={`rounded-sm ${className}`}
      style={{ width: `${size}px`, height: `${size}px` }}
      onLoad={handleImageLoad}
      onError={handleFaviconError}
    />
  );
};

export default FaviconImage;